/*!
 * Abstract base class of tko Provider (HTML <-> Data Binding linker) 🥊  tko.provider@4.0.0-alpha5c
 * (c) The Knockout.js Team - https://tko.io/
 * License: MIT (http://www.opensource.org/licenses/mit-license.php)
 */

import { options, objectMap } from 'tko.utils';
import { dependencyDetection } from 'tko.observable';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation. All rights reserved.
Licensed under the Apache License, Version 2.0 (the "License"); you may not use
this file except in compliance with the License. You may obtain a copy of the
License at http://www.apache.org/licenses/LICENSE-2.0

THIS CODE IS PROVIDED ON AN *AS IS* BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
KIND, EITHER EXPRESS OR IMPLIED, INCLUDING WITHOUT LIMITATION ANY IMPLIED
WARRANTIES OR CONDITIONS OF TITLE, FITNESS FOR A PARTICULAR PURPOSE,
MERCHANTABLITY OR NON-INFRINGEMENT.

See the Apache Version 2.0 License for specific language governing permissions
and limitations under the License.
***************************************************************************** */
/* global Reflect, Promise */

var extendStatics = Object.setPrototypeOf ||
    ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
    function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };

function __extends(d, b) {
    extendStatics(d, b);
    function __() { this.constructor = d; }
    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
}

function __read(o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
}

var BindingHandlerObject = /** @class */ (function () {
    function BindingHandlerObject() {
    }
    BindingHandlerObject.prototype.set = function (nameOrObject, value) {
        if (typeof nameOrObject === 'string') {
            this[nameOrObject] = value;
        }
        else if (typeof nameOrObject === 'object') {
            if (value !== undefined) {
                options.onError(new Error('Given extraneous `value` parameter (first param should be a string, but it was an object).' + nameOrObject));
            }
            Object.assign(this, nameOrObject);
        }
        else {
            options.onError(new Error('Given a bad binding handler type: ' + nameOrObject));
        }
    };
    /**
     * The handler may have a `.` in it, e.g. `attr.title`, in which case the
     * handler is `attr`.  Otherwise it's the name given
     */
    BindingHandlerObject.prototype.get = function (nameOrDotted) {
        var _a = __read(nameOrDotted.split('.'), 1), name = _a[0];
        return this[name];
    };
    return BindingHandlerObject;
}());

var Provider = /** @class */ (function () {
    function Provider(params) {
        if (params === void 0) { params = {}; }
        if (this.constructor === Provider) {
            throw new Error('Provider is an abstract base class.');
        }
        if (!('FOR_NODE_TYPES' in this)) {
            // FOR_NODE_TYPES must return a list of integers corresponding to the
            // node.nodeType's that the provider handles.
            throw new Error('Providers must have FOR_NODE_TYPES property');
        }
        this.bindingHandlers = params.bindingHandlers || new BindingHandlerObject();
        this.globals = params.globals || {};
    }
    Provider.prototype.setGlobals = function (globals) {
        this.globals = globals;
    };
    Provider.prototype.nodeHasBindings = function ( /* node */) { };
    Provider.prototype.getBindingAccessors = function ( /* node, context */) { };
    /**
     * Preprocess a given node.
     * @param {HTMLElement} node
     * @returns {[HTMLElement]|undefined}
     */
    Provider.prototype.preprocessNode = function (node) { };
    Provider.prototype.postProcess = function ( /* node */) { };
    Object.defineProperty(Provider.prototype, "instance", {
        /** For legacy binding provider assignments to
         *  ko.bindingProvider.instance = ... */
        get: function () { return this._overloadInstance || this; },
        set: function (provider) {
            if (!provider || provider === this) {
                this._overloadInstance = undefined;
            }
            else {
                this._overloadInstance = new LegacyProvider(provider, this);
            }
        },
        enumerable: true,
        configurable: true
    });
    // Given a function that returns bindings, create and return a new object that contains
    // binding value-accessors functions. Each accessor function calls the original function
    // so that it always gets the latest value and all dependencies are captured. This is used
    // by ko.applyBindingsToNode and getBindingsAndMakeAccessors.
    Provider.prototype.makeAccessorsFromFunction = function (callback) {
        return objectMap(dependencyDetection.ignore(callback), function (value, key) { return function () { return callback()[key]; }; });
    };
    // Returns the valueAccessor function for a binding value
    Provider.prototype.makeValueAccessor = function (value) {
        return function () { return value; };
    };
    // Given a bindings function or object, create and return a new object that contains
    // binding value-accessors functions. This is used by ko.applyBindingsToNode.
    Provider.prototype.makeBindingAccessors = function (bindings, context, node) {
        if (typeof bindings === 'function') {
            return this.makeAccessorsFromFunction(bindings.bind(null, context, node));
        }
        else {
            return objectMap(bindings, this.makeValueAccessor);
        }
    };
    return Provider;
}());
/**
 * LegacyProvider class is created when ko.bindingProvider.instance assigned to
 * an object that were once used for binding pre-4.0 binding providers e.g.
 * {  getBindings: function () { ... },
 *    nodeHasBindings: function () { ... }
 *    preprocessNode: function () { ... }
 * }
 */
var LegacyProvider = /** @class */ (function (_super) {
    __extends(LegacyProvider, _super);
    function LegacyProvider(providerObject, parentProvider) {
        var _this = _super.call(this) || this;
        Object.assign(_this, { providerObject: providerObject });
        _this.bindingHandlers = providerObject.bindingHandlers || parentProvider.bindingHandlers;
        return _this;
    }
    Object.defineProperty(LegacyProvider.prototype, "FOR_NODE_TYPES", {
        get: function () { return [1, 3, 8]; },
        enumerable: true,
        configurable: true
    });
    // This function is used if the binding provider doesn't include a getBindingAccessors function.
    // It must be called with 'this' set to the provider instance.
    LegacyProvider.prototype.getBindingsAndMakeAccessors = function (node, context) {
        var bindingsFn = this.providerObject.getBindings.bind(this.providerObject, node, context);
        return this.makeAccessorsFromFunction(bindingsFn);
    };
    LegacyProvider.prototype.getBindingAccessors = function (node, context) {
        return this.providerObject.getBindingAccessors
            ? this.providerObject.getBindingAccessors(node, context)
            : this.getBindingsAndMakeAccessors(node, context);
    };
    LegacyProvider.prototype.nodeHasBindings = function (node) {
        return this.providerObject.nodeHasBindings(node);
    };
    LegacyProvider.prototype.preprocessNode = function (node) {
        if (this.providerObject.preprocessNode) {
            return this.providerObject.preprocessNode(node);
        }
    };
    return LegacyProvider;
}(Provider));

export { Provider };
//# sourceMappingURL=tko.provider.js.map
